import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Teams from '../TeamsEnum';
import CopaArgentinaTeams from '../CopaArgentinaTeams';

const Torneos = {
  LIGA_PROFESIONAL: "Liga Profesional Argentina",
  COPA_ARGENTINA: "Copa Argentina",
  COPA_SUDAMERICANA: "Copa Sudamericana",
  COPA_LIBERTADORES: "Copa Libertadores",
};

const Statuses = {
  PROGRAMADO: "Programado",
  FINALIZADO: "Finalizado",
  POSTERGADO: "Postergado",
  SUSPENDIDO: "Suspendido",
};

const Phases = {
  FASE_REGULAR: "Fase Regular",
  PLAYOFFS: "Playoffs",
};

const statusBadge = {
  Programado: 'bg-primary',
  Finalizado: 'bg-success',
  Postergado: 'bg-warning text-dark',
  Suspendido: 'bg-danger',
};

const FixtureTable = ({ fixtures, onDelete, onEdit }) => {
  const [sortedFixtures, setSortedFixtures] = useState([]);
  const [sortAsc, setSortAsc] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '', torneo: '', jornada: '', vsTeam: '', isHome: true,
    date: '', matchHours: '', venue: '', result: '', status: 'Programado',
  });

  useEffect(() => {
    const sorted = [...fixtures].sort((a, b) => {
      if (a.jornada !== b.jornada) return sortAsc ? a.jornada - b.jornada : b.jornada - a.jornada;
      return sortAsc ? new Date(a.date) - new Date(b.date) : new Date(b.date) - new Date(a.date);
    });
    setSortedFixtures(sorted);
  }, [fixtures, sortAsc]);

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowDialog(true);
  };

  const confirmDelete = () => {
    onDelete(selectedId);
    setShowDialog(false);
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    // Handle dd/MM/yyyy format from API
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      return new Date(year, month - 1, day);
    }
    // Handle yyyy-MM-dd ISO format
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const handleEditClick = (fixture) => {
    setFormData({ ...fixture });
    setEditMode(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await onEdit({ ...formData, jornada: Number(formData.jornada), phase: formData.phase || null });
    setEditMode(false);
  };

  if (!editMode) {
    return (
      <>
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th
                  onClick={() => setSortAsc(!sortAsc)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Jornada {sortAsc ? '↑' : '↓'}
                </th>
                <th>Torneo</th>
                <th>Local / Visitante</th>
                <th>Versus</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Estadio</th>
                <th>Resultado</th>
                <th>Fase</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedFixtures.map((f) => (
                <tr key={f.id}>
                  <td>{f.jornada}</td>
                  <td>{f.torneo}</td>
                  <td>
                    <span className={`badge ${f.isHome ? 'bg-success' : 'bg-secondary'}`}>
                      {f.isHome ? 'Local' : 'Visitante'}
                    </span>
                  </td>
                  <td>{f.vsTeam}</td>
                  <td>{f.date}</td>
                  <td>{f.matchHours}</td>
                  <td>{f.venue || '-'}</td>
                  <td>{f.result || '-'}</td>
                  <td>{f.phase || '-'}</td>
                  <td>
                    <span className={`badge ${statusBadge[f.status] || 'bg-secondary'}`}>
                      {f.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleEditClick(f)} className="btn btn-warning btn-sm">
                        Editar
                      </button>
                      <button onClick={() => handleDeleteClick(f.id)} className="btn btn-danger btn-sm">
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showDialog && (
          <div className="modal" style={modalStyles}>
            <div className="modal-content" style={modalContentStyles}>
              <h4>¿Estás seguro?</h4>
              <p>Una vez borrado no se puede recuperar</p>
              <div className="modal-footer">
                <button onClick={confirmDelete} className="btn btn-danger">OK</button>
                <button onClick={() => setShowDialog(false)} className="btn btn-secondary">Cancelar</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="edit-form">
      <h3>Editar Partido del Fixture</h3>
      <form onSubmit={handleEditSubmit}>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Torneo</label>
            <select className="form-control" name="torneo" value={formData.torneo} onChange={handleInputChange}>
              {Object.entries(Torneos).map(([k, v]) => (
                <option key={k} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2 mb-3">
            <label className="form-label">Jornada</label>
            <input
              type="number"
              className="form-control"
              name="jornada"
              min="1"
              value={formData.jornada}
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-4 mb-3">
            <label className="form-label">Versus</label>
            <select className="form-control" name="vsTeam" value={formData.vsTeam} onChange={handleInputChange}>
              {Object.entries(formData.torneo === "Copa Argentina" ? CopaArgentinaTeams : Teams).map(([k, v]) => (
                <option key={k} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2 mb-3">
            <label className="form-label">Local / Visitante</label>
            <select
              className="form-control"
              name="isHome"
              value={formData.isHome}
              onChange={(e) => setFormData((prev) => ({ ...prev, isHome: e.target.value === 'true' }))}>
              <option value="true">Local</option>
              <option value="false">Visitante</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 mb-3">
            <label className="form-label">Fecha</label>
            <DatePicker
              selected={parseDateString(formData.date)}
              className="form-control"
              name="date"
              dateFormat="dd-MM-yyyy"
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, date: date.toISOString().split('T')[0] }))
              }
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">Hora Partido</label>
            <input
              type="time"
              className="form-control"
              name="matchHours"
              value={formData.matchHours}
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">Estadio</label>
            <input
              type="text"
              className="form-control"
              name="venue"
              value={formData.venue || ''}
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-3 mb-3">
            <label className="form-label">Estado</label>
            <select className="form-control" name="status" value={formData.status} onChange={handleInputChange}>
              {Object.entries(Statuses).map(([k, v]) => (
                <option key={k} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 mb-3">
            <label className="form-label">Resultado</label>
            <input
              type="text"
              className="form-control"
              name="result"
              value={formData.result || ''}
              onChange={handleInputChange}
              placeholder="Ej: 2-1"
            />
          </div>

          {formData.torneo === "Liga Profesional Argentina" && (
            <div className="col-md-3 mb-3">
              <label className="form-label">Fase</label>
              <select className="form-control" name="phase" value={formData.phase || ''} onChange={handleInputChange}>
                <option value="">Sin fase</option>
                {Object.entries(Phases).map(([k, v]) => (
                  <option key={k} value={v}>{v}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn"
          style={{ backgroundColor: '#C73B33', borderColor: '#FF5733', color: '#FFF' }}>
          Guardar Cambios
        </button>
        <button
          type="button"
          className="btn btn-secondary ms-2"
          onClick={() => setEditMode(false)}>
          Cancelar
        </button>

        <div style={{ height: '80px' }}></div>
      </form>
    </div>
  );
};

const modalStyles = {
  position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
  justifyContent: 'center', alignItems: 'center',
};

const modalContentStyles = {
  backgroundColor: 'white', padding: '50px', borderRadius: '5px', textAlign: 'center',
};

export default FixtureTable;
